import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  MessageSquareText,
  RadioTower,
  Route,
  ShieldAlert,
  Truck,
} from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { CommandTraceView } from "@/components/headless/CommandTraceView";
import { EscalationQueue } from "@/components/headless/EscalationQueue";
import { TenderTimeline } from "@/components/headless/TenderTimeline";
import { getEmptyListState } from "@/core/ui/empty-states";
import { BulkActionsPanel } from "./BulkActionsPanel";
import { SavedViewsPanel } from "./SavedViewsPanel";
import type { ApprovalSource } from "@/core/approvals/unified-approvals";
import type { getConsoleSnapshot } from "@/lib/demo-data";

type Snapshot = ReturnType<typeof getConsoleSnapshot>;

export function ConsoleDashboard({
  tenantId,
  snapshot,
  currentUserId,
  onDecide,
}: {
  tenantId: string;
  snapshot: Snapshot;
  /** Server-derived authenticated user id: undefined = resolving, null = signed out. */
  currentUserId?: string | null;
  onDecide?: (args: { id: string; source: ApprovalSource; status: string }) => void;
}) {
  const openExceptions = snapshot.loads.flatMap((item) => item.exceptions);
  const firstLoadEvidence = snapshot.loads[0]?.evidence ?? [];
  const firstLoad = snapshot.loads[0]?.load;
  const avgHealth = Math.round(
    snapshot.loads.length === 0
      ? 0
      : snapshot.loads.reduce((total, item) => total + item.confidence.loadHealthScore, 0) /
          snapshot.loads.length,
  );
  const commandTrace = {
    traceId: `trace_${firstLoad?.id ?? tenantId}_cover_load`,
    status: snapshot.approvals.length > 0 ? "approval_required" : "dry_run",
    routeName: "cover_load",
    commandName: snapshot.approvals[0]?.requestedAction ?? "prepare_tender",
    policyReasons: [
      snapshot.approvals[0]?.risk,
      snapshot.permissionExamples[1]?.reasons[0],
      "external commits require approval",
    ].filter((item): item is string => Boolean(item)),
    evidenceRefs: firstLoadEvidence.slice(0, 4).map((item) => item.id),
    payloadHash: `hash_${firstLoad?.id ?? tenantId}`,
  };
  const tenderEvents = [
    { id: "read", label: "Read load context", status: "complete" as const, commandName: "read_load" },
    { id: "capacity", label: "Search and rank capacity", status: "complete" as const, commandName: "search_capacity" },
    {
      id: "approval",
      label: "Hold tender for dispatcher approval",
      status: snapshot.approvals.length > 0 ? ("current" as const) : ("pending" as const),
      commandName: "request_dispatch_approval",
    },
    { id: "tender", label: "Prepare tender packet", status: "pending" as const, commandName: "prepare_tender" },
  ];
  const escalationItems = openExceptions.slice(0, 4).map((exception) => ({
    id: exception.id,
    title: exception.title,
    severity: exception.severity,
    reason: exception.detectedReason,
    recommendedAction: exception.recommendedAction,
    dueLabel: `SLA ${exception.slaDueAt}`,
  }));

  return (
    <div className="space-y-8">
      <section id="command" className="grid gap-4 md:grid-cols-4">
        <Metric label="Active loads" value={String(snapshot.loads.length)} detail="Live workspace" />
        <Metric label="Open exceptions" value={String(openExceptions.length)} detail="Needs review" />
        <Metric label="Avg health" value={`${avgHealth}%`} detail="Signal quality" />
        <Metric
          label="Load-board postings"
          value={String(snapshot.loadBoard.length)}
          detail="DAT + Truckstop"
        />
      </section>

      <SavedViewsPanel
        tenantId={tenantId}
        userId={currentUserId}
        loads={snapshot.loads.map(({ load, confidence, exceptions }) => ({
          ...load,
          exceptionCount: exceptions.length,
          currentRiskScore: confidence.exceptionRiskScore,
        }))}
      />

      <BulkActionsPanel
        tenantId={tenantId}
        userId={currentUserId}
        loads={snapshot.loads.map(({ load, exceptions }) => ({
          id: load.id,
          reference: load.reference,
          lifecycleState: load.lifecycleState,
          customerSensitive: load.customerSensitive,
          exceptionCount: exceptions.length,
          hoursSinceLastUpdate: load.lifecycleState === "in_transit" ? 7 : 1,
        }))}
      />

      <section className="grid gap-5 xl:grid-cols-[1.5fr_1fr]">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Command Center</CardTitle>
            <Button variant="secondary">
              <ArrowUpRight size={16} />
              Run lifecycle tick
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {snapshot.loads.length === 0 ? (
              <EmptyState {...getEmptyListState("activeLoads")} />
            ) : (
              snapshot.loads.map(({ load, confidence, exceptions }) => (
                <div
                  key={load.id}
                  className="grid gap-3 rounded-lg border border-border p-4 md:grid-cols-[1fr_auto]"
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{load.reference}</span>
                      <Badge>{load.lifecycleState.replaceAll("_", " ")}</Badge>
                      {exceptions.length > 0 ? (
                        <Badge variant="warn">{exceptions.length} exception</Badge>
                      ) : (
                        <Badge variant="good">on track</Badge>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      {load.origin.city}, {load.origin.state} to {load.destination.city},{" "}
                      {load.destination.state} via {load.carrierName}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-right text-sm">
                    <MiniScore label="Health" value={confidence.loadHealthScore} />
                    <MiniScore label="Visibility" value={confidence.visibilityConfidence} />
                    <MiniScore label="ETA" value={confidence.etaConfidence} />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Action Executor</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ActionRow
              icon={<MessageSquareText size={16} />}
              title="Carrier ETA request"
              detail={snapshot.permissionExamples[0]?.reasons[0] ?? "allowed"}
              status="allow"
            />
            <ActionRow
              icon={<ShieldAlert size={16} />}
              title="Customer update"
              detail={snapshot.permissionExamples[1]?.reasons[0] ?? "approval"}
              status="approval"
            />
            <ActionRow
              icon={<Truck size={16} />}
              title="Carrier booking"
              detail="commitment actions route to approval"
              status="approval"
            />
          </CardContent>
        </Card>
      </section>

      <section id="headless" className="grid gap-5 xl:grid-cols-[1fr_1fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Headless Trace</CardTitle>
          </CardHeader>
          <CardContent>
            <CommandTraceView {...commandTrace} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Tender Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <TenderTimeline events={tenderEvents} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Escalation Queue</CardTitle>
          </CardHeader>
          <CardContent>
            <EscalationQueue items={escalationItems} />
          </CardContent>
        </Card>
      </section>

      <section id="load-board" className="space-y-4">
        <SectionTitle title="Load Board Workspace" detail="DAT and Truckstop adapter surface" />
        <div className="grid gap-4 lg:grid-cols-2">
          {snapshot.loadBoard.length === 0 ? (
            <EmptyState className="lg:col-span-2" {...getEmptyListState("loadBoard")} />
          ) : (
            snapshot.loadBoard.map((item) => (
              <Card key={`${item.provider}-${item.loadRef}`}>
                <CardContent className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold">{item.provider}</div>
                      <div className="text-sm text-muted-foreground">{item.loadRef}</div>
                    </div>
                    <Badge variant={item.status === "Posted" ? "good" : "info"}>{item.status}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <MiniText label="Capacity" value={String(item.capacity)} />
                    <MiniText label="Best fit" value={item.bestFit} />
                    <MiniText label="Target" value={item.targetRate} />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </section>

      <section id="margin" className="space-y-4">
        <SectionTitle title="Margin Reports" detail="Load charges grouped by customer, lane, and carrier" />
        <div className="grid gap-4 xl:grid-cols-3">
          <MarginTable title="By Customer" rows={snapshot.marginReports.byCustomer} />
          <MarginTable title="By Lane" rows={snapshot.marginReports.byLane} />
          <MarginTable title="By Carrier" rows={snapshot.marginReports.byCarrier} />
        </div>
      </section>

      <section id="exceptions" className="space-y-4">
        <SectionTitle title="Exception Workbench" detail="Operational issue coverage" />
        <div className="grid gap-4 lg:grid-cols-2">
          {openExceptions.length === 0 ? (
            <EmptyState className="lg:col-span-2" {...getEmptyListState("exceptions")} />
          ) : (
            openExceptions.map((exception) => (
              <Card key={exception.id}>
                <CardContent className="space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-3">
                      <AlertTriangle className="mt-1 text-amber-600" size={18} />
                      <div>
                        <div className="font-semibold">{exception.title}</div>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {exception.detectedReason}
                        </p>
                      </div>
                    </div>
                    <Badge variant={exception.severity === "high" ? "bad" : "warn"}>
                      {exception.severity}
                    </Badge>
                  </div>
                  <div className="rounded-md bg-muted p-3 text-sm">{exception.recommendedAction}</div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </section>

      <section id="approvals" className="space-y-4">
        <SectionTitle title="Approval Queue" detail="Human decisions before customer or commitment actions" />
        {snapshot.approvals.length === 0 ? (
          <EmptyState {...getEmptyListState("approvals")} />
        ) : (
          snapshot.approvals.map((item) => (
            <Card key={item.id}>
              <CardContent className="grid gap-4 md:grid-cols-[1fr_auto]">
                <div>
                  <div className="font-semibold">{item.title}</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {item.loadRef} - {item.risk}
                  </div>
                  <div className="mt-3 rounded-md bg-muted p-3 text-sm">{item.recommendation}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => onDecide?.({ id: item.id, source: item.source, status: "edited_and_approved" })}
                  >
                    Edit &amp; Approve
                  </Button>
                  <Button onClick={() => onDecide?.({ id: item.id, source: item.source, status: "approved" })}>
                    Approve
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </section>

      <section id="timeline" className="space-y-4">
        <SectionTitle title="Load Timeline" detail="Evidence, tools, decisions, approvals, outcomes" />
        <Card>
          <CardContent className="space-y-4">
            {firstLoadEvidence.length === 0 ? (
              <EmptyState {...getEmptyListState("timeline")} />
            ) : (
              firstLoadEvidence.map((item) => (
                <div key={item.id} className="flex gap-3">
                  <div className="mt-1 flex h-7 w-7 items-center justify-center rounded-md bg-muted">
                    {item.type === "load_board" ? <Route size={14} /> : <RadioTower size={14} />}
                  </div>
                  <div>
                    <div className="text-sm font-medium">
                      {item.type.replaceAll("_", " ")} from {item.source}
                    </div>
                    <div className="text-xs text-muted-foreground">{item.receivedAt}</div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </section>

      <section id="policies" className="space-y-4">
        <SectionTitle title="Policies And Integration Health" detail="Tenant-scoped rules and provider readiness" />
        <div className="grid gap-4 xl:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Active Permission Defaults</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {snapshot.policies.map((policy) => (
                <div key={policy} className="flex gap-3 text-sm">
                  <CheckCircle2 size={16} className="mt-0.5 text-emerald-600" />
                  <span>{policy}</span>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Integration Health</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              {["DAT", "Truckstop", "FMCSA", "SendGrid", "Twilio", "ELD"].map((name) => (
                <div key={name} className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium">{name}</span>
                    <Badge variant={name === "ELD" ? "warn" : "info"}>
                      {name === "ELD" ? "sample" : "scaffolded"}
                    </Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <Card>
      <CardContent>
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className="mt-2 text-3xl font-semibold tracking-tight">{value}</div>
        <div className="mt-1 text-xs text-muted-foreground">{detail}</div>
      </CardContent>
    </Card>
  );
}

function MiniScore({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="font-mono text-lg font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function MiniText({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 line-clamp-2 font-medium">{value}</div>
    </div>
  );
}

function SectionTitle({ title, detail }: { title: string; detail: string }) {
  return (
    <div>
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="text-sm text-muted-foreground">{detail}</p>
    </div>
  );
}

function MarginTable({
  title,
  rows,
}: {
  title: string;
  rows: Snapshot["marginReports"]["byCustomer"];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.length === 0 ? (
          <EmptyState className="p-4" {...getEmptyListState("marginRows")} />
        ) : (
          rows.slice(0, 5).map((row) => (
            <div key={row.key} className="grid grid-cols-[1fr_auto] gap-3 rounded-md border border-border p-2 text-sm">
              <div>
                <div className="font-medium">{row.key}</div>
                <div className="text-xs text-muted-foreground">{row.loadCount} loads</div>
              </div>
              <div className="text-right">
                <div className="font-medium">{formatMoney(row.marginCents)}</div>
                <div className="text-xs text-muted-foreground">{row.marginPct}%</div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function formatMoney(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(
    cents / 100,
  );
}

function ActionRow({
  icon,
  title,
  detail,
  status,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
  status: "allow" | "approval";
}) {
  return (
    <div className="flex gap-3 rounded-lg border border-border p-3">
      <div className="mt-0.5 text-muted-foreground">{icon}</div>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{title}</span>
          <Badge variant={status === "allow" ? "good" : "warn"}>
            {status === "allow" ? "allow" : "approval"}
          </Badge>
        </div>
        <div className="mt-1 text-xs text-muted-foreground">{detail}</div>
      </div>
    </div>
  );
}
